import {AxiosResponse} from "axios";
import {api, getApiError, handleApiError, ServiceResponse} from "../ApiClient";

export interface UpdateUser {
    firstName: string
    lastName: string
    phoneNumber: string
    bio: string
    nationality: string
    languages: string[]
    headline: string
    instagramHandle: string
    twitterHandle: string
    background: string
    clubExperience: number
    tourExperience: number
}

export interface User {
    id: string
    firstName: string
    lastName: string
    displayName: string
    email: string
    phoneNumber: string
    bio: string
    nationality: string
    languages: string[]
    headline: string
    instagramHandle: string
    twitterHandle: string
    background: string
    clubExperience: number
    tourExperience: number
}

export interface GetUserResponse extends ServiceResponse {
    user?: User
}

export interface AvatarResponse extends ServiceResponse {
    avatar?: string
}

export interface CreateUserResponse extends ServiceResponse {
    user?: User
}

export const UserService = () => {

    const getCurrentUser = (): Promise<GetUserResponse> => {
        return api.get<User>(`/api/1/user`)
            .then((resp) => {
                if (resp && resp.status === 200 && resp.data) {
                    return {
                        user: resp.data
                    }
                }

                // this should probably be replicated throughout the code base...
                // does this mean no api error messages are getting sent to the app?
                let e = getApiError(resp)
                return {
                    error: {
                        apiError: e,
                        message: e.message ? e.message : "Unknown error occurred."
                    }
                }
            })
            .catch(handleApiError)
    };

    const getAvatar = async (userId: string): Promise<AvatarResponse> => {
        return api.get<string>(`/api/1/user/${userId}/avatar/new`)
            .then((resp) => {
                if (resp && resp.status === 200 && resp.data) {
                    return {
                        avatar: resp.data
                    }
                }

                return {
                    error: {
                        message: 'Could not get avatar.'
                    }
                }
            })
            .catch(handleApiError)
    };

    const createUser = async (firstName: string, lastName: string): Promise<CreateUserResponse> => {
        return api.post<User>(`/api/1/user`, {
            firstName: firstName,
            lastName: lastName
        })
            .then((resp) => {
                if (resp && resp.status === 200 && resp.data) {
                    return {
                        user: resp.data
                    }
                }

                return {
                    error: {
                        message: 'Could not create user profile.'
                    }
                }
            })
            .catch(handleApiError)
    };

    const updateUser = async (id: string, user: UpdateUser): Promise<CreateUserResponse> => {
        return api.put<User>(`/api/1/user/${id}`, {
            ...user,
            id
        })
            .then((resp) => {
                if (resp && resp.status === 200 && resp.data) {
                    return {
                        user: resp.data
                    }
                }

                return {
                    error: {
                        message: 'Could not update user profile.'
                    }
                }
            })
            .catch(handleApiError)
    };

    const uploadAvatar = async (uuid: string, imageUri: any): Promise<ServiceResponse> => {
        // ImagePicker saves the taken photo to disk and returns a local URI to it
        let filename = imageUri.split('/').pop();

        // Infer the type of the image
        let match = /\.(\w+)$/.exec(filename);
        let type = match ? `image/${match[1]}` : `image`;
        let blob = await (await fetch(imageUri)).blob();

        return api.put<string>(`/api/1/user/${uuid}/avatar/new`)
            .then((resp: AxiosResponse<string>) => {
                return fetch(resp.data, {
                    method: 'PUT',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': type
                    },
                    body: blob
                }).then((resp) => {
                    if (resp.status == 200) {
                        return {}
                    }

                    return {
                        error: {
                            message: 'Unknown error occurred uploading avatar.'
                        }
                    }
                }).catch(() => {
                    return {
                        error: {
                            message: 'Unknown error occurred uploading avatar.'
                        }
                    }
                })
            })
            .catch(handleApiError)
    };


    const deleteUser = async (userId: string): Promise<ServiceResponse> => {
        return api.delete(`/api/1/user/${userId}`)
            .then((resp) => {
                if (resp && resp.status === 202) {
                    return {}
                }

                return {
                    error: {
                        message: 'Could not get avatar.'
                    }
                }
            })
            .catch(handleApiError)
    };

    return {
        getCurrentUser,
        getAvatar,
        createUser,
        updateUser,
        uploadAvatar,
        deleteUser
    }
}
